import ResizeObserver from "resize-observer-polyfill";
import { useLayoutEffect } from "react";
import debounce from "lodash/debounce";
/** Adds a classNames to the start and end of elements in a horizontal row.
 * first wrapping child in each row.
 * Useful for dynamic styling like hiding dividing lines on wrapped rows */
export const useTagRowWraps = (ref) => {
    /* useLayoutEffect prevents flashing of unstyled elements. Do not change to useEffect */
    useLayoutEffect(() => {
        if (!ref.current || process.env.NODE_ENV === "test") {
            return;
        }
        const element = ref.current;
        const tagRowWraps = debounce(() => {
            let isWrappedRow = false;
            // Reset all classNames
            const children = Array.from(element.children);
            children.forEach((child) => child.classList.remove(IS_START_OF_ROW_CLASS_NAME, IS_END_OF_ROW_CLASS_NAME, IS_WRAPPED_ROW_CLASS_NAME, IS_FIRST_ROW_CLASS_NAME, IS_LAST_ROW_CLASS_NAME));
            // Tag start element of each row and each element in a wrapped row
            let lastOffset = children[0].offsetTop;
            children.forEach((child) => {
                // if child offset is different than the last one add a className to the child
                if (child.offsetTop === lastOffset) {
                    if (!isWrappedRow) {
                        child.classList.add(IS_FIRST_ROW_CLASS_NAME);
                    }
                }
                else {
                    isWrappedRow = true;
                    child.classList.add(IS_START_OF_ROW_CLASS_NAME);
                }
                // Tag wrapped row items
                if (isWrappedRow) {
                    child.classList.add(IS_WRAPPED_ROW_CLASS_NAME);
                }
                lastOffset = child.offsetTop;
            });
            // Tag end element of each row
            let isLastRow = true;
            lastOffset = children[0].offsetTop;
            children.reverse().forEach((child) => {
                if (isLastRow) {
                    child.classList.add(IS_LAST_ROW_CLASS_NAME);
                }
                if (child.offsetTop !== lastOffset) {
                    isLastRow = false;
                    child.classList.add(IS_END_OF_ROW_CLASS_NAME);
                }
                lastOffset = child.offsetTop;
            });
        }, 120, { leading: true, trailing: true });
        const mutationObserver = new MutationObserver(tagRowWraps);
        const resizeObserver = new ResizeObserver(tagRowWraps);
        resizeObserver.observe(ref.current);
        mutationObserver.observe(ref.current, {
            attributes: true,
            attributeFilter: ["class", "style"],
            childList: true,
            subtree: false
        });
        return () => {
            tagRowWraps.cancel();
            resizeObserver.disconnect();
            mutationObserver.disconnect();
        };
    }, [ref]);
};
export const IS_START_OF_ROW_CLASS_NAME = "AUI_is-start-of-row";
export const IS_END_OF_ROW_CLASS_NAME = "AUI_is-end-of-row";
export const IS_WRAPPED_ROW_CLASS_NAME = "AUI_is-wrapped-row";
export const IS_FIRST_ROW_CLASS_NAME = "AUI_is-first-row";
export const IS_LAST_ROW_CLASS_NAME = "AUI_is-last-row";
